import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import {
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
  RailVisitResponseDto,
  VesselVisitDto,
} from '@planning/app/api'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import { railVisitService, vesselVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'

export type TransportSelectionProps = {
  direction: CarrierVisitDirection
  disabled?: boolean
  carrierType?: string
  carrierVisitId?: number
  onChangeCarrierType: (value: string) => void
  onChangeCarrierVisit: (value: any) => void
}

export const TransportSelection: FC<TransportSelectionProps> = observer(props => {
  const { t } = useTranslate()
  const [selectedVisit, setSelectedVisit] = useState<
    RailVisitResponseDto | VesselVisitDto | undefined
  >(undefined)

  const {
    direction,
    disabled = false,
    carrierType,
    carrierVisitId,
    onChangeCarrierType,
    onChangeCarrierVisit,
  } = props

  useAsyncFetch(async () => {
    if (carrierVisitId) {
      if (carrierType === CarrierType.Train) {
        const railVisit = await railVisitService.getById(carrierVisitId)
        setSelectedVisit(railVisit)
        return
      }

      if (carrierType === CarrierType.Vessel) {
        const vesselVisit = await vesselVisitService.getById(carrierVisitId)
        setSelectedVisit(vesselVisit)
        return
      }
    }
  }, [carrierType, carrierVisitId])

  return (
    <Grid container spacing={2} direction={'column'}>
      <Grid size={12}>
        <FormControl fullWidth>
          <InputLabel id='carrier-select'>
            {direction === CarrierVisitDirection.Inbound
              ? t('inboundCarrierType', 'Inbound carrier type')
              : t('outboundCarrierType', 'Outbound carrier type')}
          </InputLabel>
          <Select
            labelId='carrier-select'
            disabled={disabled}
            label={t('inboundCarrierType', 'Inbound carrier type')}
            value={carrierType || ''}
            onChange={e => {
              onChangeCarrierType(e.target.value as string)
              onChangeCarrierVisit(null)
              setSelectedVisit(undefined)
            }}
            data-cy='change-transport-select'
          >
            <MenuItem value={CarrierType.Train} data-cy='Train-option'>
              {t('train', 'Train')}
            </MenuItem>
            <MenuItem value={CarrierType.Truck} data-cy='Truck-option'>
              {t('truck', 'Truck')}
            </MenuItem>
            <MenuItem value={CarrierType.Vessel} data-cy='Vessel-option'>
              {t('vessel', 'Vessel')}
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {carrierType === CarrierType.Train && (
        <Grid size={12}>
          <RailVisitAutoComplete
            disabled={disabled}
            selectedRailVisit={selectedVisit as RailVisitResponseDto}
            handleOnChange={visit => {
              onChangeCarrierVisit(visit?.id ?? null)
            }}
            cargoType='Container'
            statuses={[
              CarrierVisitStatus.Expected,
              CarrierVisitStatus.InOperation,
              CarrierVisitStatus.Arrived,
            ]}
          />
        </Grid>
      )}
      {carrierType === CarrierType.Vessel && (
        <Grid size={12}>
          <VesselVisitAutoComplete
            disabled={disabled}
            selectedVesselVisit={selectedVisit as VesselVisitDto}
            handleOnChange={visit => {
              onChangeCarrierVisit(visit?.id ?? null)
            }}
            cargoType='Container'
            hideBlockedForEdit
          />
        </Grid>
      )}
    </Grid>
  )
})
